import { Add, DeleteOutline, ManageAccounts } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { get } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { deleteWallet, getWalletsByPortfolio } from '../../http/WalletsApi';
import { getPortfolios } from '../../http/PortfoliosApi';
import { CHAIN_LABELS } from './addressValidation';
import AddWalletDialog from './dialogs/AddWalletDialog';
import ManageAddressesDialog from './dialogs/ManageAddressesDialog';

const summarizeAddresses = (addresses) => {
  if (!Array.isArray(addresses) || addresses.length === 0) return 'No addresses';
  const counts = addresses.reduce((acc, { chain }) => {
    acc[chain] = (acc[chain] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts)
    .map(([chain, count]) => `${count} ${CHAIN_LABELS[chain] || chain}`)
    .join(' · ');
};

const SPACING = {
  XS: 1,
  SM: 2,
  MD: 3,
  LG: 4,
};

const DEFAULT_ERROR_MESSAGE = 'An error happened!';

export default function WalletsComponent() {
  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [snackState, setSnackState] = useState({
    open: false,
    message: DEFAULT_ERROR_MESSAGE,
  });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [manageWallet, setManageWallet] = useState(null);

  const handleSnackStateChange = useCallback((key, value) => {
    setSnackState((prev) => ({ ...prev, [key]: value }));
  }, []);

  useEffect(() => {
    async function loadPortfolios() {
      try {
        const { data } = await getPortfolios();
        setPortfolios(data);
        if (data && data.length > 0) setSelectedPortfolio(data[0].id);
      } catch (err) {
        handleSnackStateChange(
          'message',
          get(err, 'response.data.message', DEFAULT_ERROR_MESSAGE),
        );
        handleSnackStateChange('open', true);
      }
    }

    loadPortfolios();
  }, [handleSnackStateChange]);

  useEffect(() => {
    async function loadWallets() {
      if (!selectedPortfolio) return;
      try {
        const { data } = await getWalletsByPortfolio(selectedPortfolio);
        // getWalletsByPortfolio returns a flat array of wallets with embedded addresses
        setWallets(Array.isArray(data) ? data : []);
      } catch (err) {
        handleSnackStateChange(
          'message',
          get(err, 'response.data.message', DEFAULT_ERROR_MESSAGE),
        );
        handleSnackStateChange('open', true);
      }
    }

    loadWallets();
  }, [selectedPortfolio, handleSnackStateChange]);

  const handleWalletAdded = useCallback(
    (wallet) => {
      setWallets((prev) => [...prev, { addresses: [], ...wallet }]);
    },
    [setWallets],
  );

  const handleAddressesChange = useCallback((walletId, addresses) => {
    setWallets((prev) =>
      prev.map((w) => (w.id === walletId ? { ...w, addresses } : w)),
    );
    setManageWallet((prev) =>
      prev && prev.id === walletId ? { ...prev, addresses } : prev,
    );
  }, []);

  const deleteRow = useCallback(
    async (id) => {
      try {
        await deleteWallet(id);
        setWallets((prev) => prev.filter((w) => w.id !== id));
      } catch (err) {
        handleSnackStateChange(
          'message',
          get(err, 'response.data.message', DEFAULT_ERROR_MESSAGE),
        );
        handleSnackStateChange('open', true);
      }
    },
    [handleSnackStateChange],
  );

  const columns = [
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'addresses',
      headerName: 'Addresses',
      flex: 2,
      sortable: false,
      valueGetter: (value) => summarizeAddresses(value),
    },
    {
      field: 'portfolioId',
      headerName: 'Portfolio',
      flex: 1,
      valueGetter: (value) => value ?? '',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      flex: 0.8,
      renderCell: ({ row }) => (
        <>
          <IconButton
            onClick={() => setManageWallet(row)}
            size="small"
            title="Manage addresses"
          >
            <ManageAccounts />
          </IconButton>
          <IconButton
            onClick={() => deleteRow(row.id)}
            size="small"
            title="Delete wallet"
          >
            <DeleteOutline />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ p: SPACING.LG }}>
      <Stack spacing={SPACING.MD}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5">Wallets</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Select
              value={selectedPortfolio || ''}
              onChange={(e) => setSelectedPortfolio(e.target.value)}
              size="small"
            >
              {portfolios.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setAddDialogOpen(true)}
            >
              Add wallet
            </Button>
          </Box>
        </Box>

        <Paper sx={{ p: SPACING.MD, backgroundColor: '#ffffff' }}>
          <div style={{ width: '100%' }}>
            <DataGrid
              autoHeight
              rows={wallets}
              columns={columns}
              getRowId={(row) => row.id}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              localeText={{ noRowsLabel: 'No wallets available' }}
            />
          </div>
        </Paper>
      </Stack>

      <AddWalletDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        portfolios={portfolios}
        defaultPortfolioId={selectedPortfolio}
        onWalletAdded={handleWalletAdded}
        setErrMessage={(m) => handleSnackStateChange('message', m)}
        errMessage={snackState.message}
        setSnackOpen={(open) => handleSnackStateChange('open', open)}
      />

      <ManageAddressesDialog
        open={Boolean(manageWallet)}
        onClose={() => setManageWallet(null)}
        wallet={manageWallet}
        onAddressesChange={handleAddressesChange}
        setErrMessage={(m) => handleSnackStateChange('message', m)}
        setSnackOpen={(open) => handleSnackStateChange('open', open)}
      />

      <Snackbar
        open={snackState.open}
        autoHideDuration={6000}
        onClose={() => handleSnackStateChange('open', false)}
      >
        <Alert severity="error">{snackState.message}</Alert>
      </Snackbar>
    </Box>
  );
}
